import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";
import type { PromptDto } from "@/lib/queries";
import { usePromptActions } from "@/features/prompts/hooks/usePromptActions";

interface DeletePromptDialogProps {
  prompt: PromptDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

/** `@nexus/ui` has no AlertDialog, so this is a Dialog with a destructive confirm. */
export function DeletePromptDialog({
  prompt,
  open,
  onOpenChange,
  onDeleted,
}: DeletePromptDialogProps) {
  const { t } = useTranslation();
  const { remove } = usePromptActions();

  const handleConfirm = () => {
    remove.mutate(prompt.id, {
      onSuccess: () => {
        onOpenChange(false);
        onDeleted?.();
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t("prompts.delete.title", "Delete prompt?")}</DialogTitle>
          <DialogDescription>
            {t(
              "prompts.delete.description",
              '"{{name}}" stops resolving at {{usedIn}}, which falls back to the global prompt. Version history is kept.',
              { name: prompt.name, usedIn: prompt.usedIn },
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={remove.isPending}
            onClick={handleConfirm}
          >
            {remove.isPending
              ? t("prompts.delete.deleting", "Deleting…")
              : t("prompts.delete.confirm", "Delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
